import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  Typography,
  Chip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
  mediaBox: {
    marginTop: theme.spacing(2),
  },
  mediaButton: {
    marginTop: theme.spacing(1),
  },
  mediaPreviewRow: {
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  mediaThumb: {
    width: 56,
    height: 56,
    objectFit: "cover",
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
  },
  mediaAudio: {
    height: 40,
    maxWidth: 220,
  },
  mediaName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  variablesBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
  },
  variableTitle: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  variableChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  variableChip: {
    backgroundColor: "#ff9820",
    color: "#fff",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "#e07f12",
    },
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Too Short!")
    .max(15, "Too Long!")
    .required("Required"),
  message: Yup.string().max(30000, "Too Long!").required("Required"),
});

const quickAnswerVariables = [
  { label: "Primeiro Nome", value: "{{firstName}}" },
  { label: "Nome", value: "{{name}}" },
  { label: "Sauda\u00e7\u00e3o", value: "{{greeting}}" },
  { label: "Protocolo", value: "{{protocol}}" },
  { label: "Protocolo Aleat\u00f3rio", value: "{{randomProtocol}}" },
  { label: "Data", value: "{{date}}" },
  { label: "Hora", value: "{{time}}" },
  { label: "N\u00ba do Cliente / Ticket", value: "{{ticketId}}" },
  { label: "Setor", value: "{{queue}}" },
  { label: "Conex\u00e3o", value: "{{connection}}" },
  { label: "Quebra de Linha", value: "\n" },
];

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    shortcut: "",
    message: "",
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  // Arquivo fica fora do Formik: File não e serializavel e o enableReinitialize
  // o descartaria a cada recarga dos valores iniciais.
  const [mediaFile, setMediaFile] = useState(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const mediaInputRef = useRef(null);

  const [mediaPreview, setMediaPreview] = useState("");

  // URL.createObjectURL reserva memoria até ser revogada, e precisa ficar em
  // estado: gerar a URL no corpo do render criaria um blob novo a cada
  // renderização e o cleanup revogaria um endereço diferente do exibido.
  useEffect(() => {
    if (!mediaFile) {
      setMediaPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(mediaFile);
    setMediaPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [mediaFile]);

  const hasExistingMedia = Boolean(!removeMedia && quickAnswer.mediaUrl);
  const currentMediaName =
    (mediaFile && mediaFile.name) ||
    (hasExistingMedia && quickAnswer.mediaName) ||
    "";
  const currentMediaPreview = mediaFile
    ? mediaPreview
    : (hasExistingMedia && quickAnswer.mediaUrl) || "";
  const currentMediaType = mediaFile
    ? String(mediaFile.type || "").split("/")[0]
    : (hasExistingMedia && quickAnswer.mediaType) || "";

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!quickAnswerId) return;

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer(data);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
    setMediaFile(null);
    setRemoveMedia(false);
  };

  const handleSelectMedia = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setMediaFile(file);
    setRemoveMedia(false);
    // Zera o input para que escolher o mesmo arquivo de novo, depois de
    // remove-lo, ainda dispare o onChange.
    e.target.value = "";
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setRemoveMedia(true);
  };

  const handleSaveQuickAnswer = async (values) => {
    try {
      if (!String(values.message || "").trim()) {
        toast.warn(i18n.t("quickAnswersModal.form.emptyWarning"));
        return;
      }

      // FormData sempre: o backend le os campos de texto do req.body tanto em
      // JSON quanto em multipart, e assim ha um caminho único com ou sem anexo.
      const payload = new FormData();
      payload.append("shortcut", String(values.shortcut || "").trim());
      payload.append("message", String(values.message || ""));

      if (mediaFile) {
        payload.append("media", mediaFile);
      } else if (removeMedia) {
        payload.append("removeMedia", "true");
      }

      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, payload);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", payload);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId ? "Editar Resposta R\u00e1pida" : "Adicionar Resposta R\u00e1pida"}
        </DialogTitle>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label="Atalho"
                    name="shortcut"
                    autoFocus
                    error={touched.shortcut && Boolean(errors.shortcut)}
                    helperText={touched.shortcut && errors.shortcut}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={"Resposta R\u00e1pida"}
                    name="message"
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    multiline
                    rows={5}
                    fullWidth
                  />
                </div>
                <div className={classes.mediaBox}>
                  <Typography variant="body2" className={classes.variableTitle}>
                    {"Anexo (imagem, v\u00eddeo ou \u00e1udio)"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {currentMediaType === "audio"
                      ? "O WhatsApp não aceita legenda em audio: o texto acima sera enviado como mensagem separada, antes do audio."
                      : "O texto acima e enviado como legenda do anexo."}
                  </Typography>

                  <input
                    type="file"
                    ref={mediaInputRef}
                    style={{ display: "none" }}
                    accept="image/jpeg,image/png,image/gif,video/mp4,video/webm,audio/mpeg,audio/ogg,audio/opus,audio/wav,audio/aac,audio/mp4,audio/m4a,audio/amr,audio/webm"
                    onChange={handleSelectMedia}
                  />

                  {currentMediaPreview ? (
                    <div className={classes.mediaPreviewRow}>
                      {currentMediaType === "video" ? (
                        <video
                          src={currentMediaPreview}
                          className={classes.mediaThumb}
                          muted
                        />
                      ) : currentMediaType === "audio" ? (
                        <audio
                          src={currentMediaPreview}
                          className={classes.mediaAudio}
                          controls
                        />
                      ) : (
                        <img
                          src={currentMediaPreview}
                          alt={currentMediaName}
                          className={classes.mediaThumb}
                        />
                      )}
                      <Typography variant="body2" className={classes.mediaName}>
                        {currentMediaName}
                      </Typography>
                      <Button
                        size="small"
                        color="secondary"
                        onClick={handleRemoveMedia}
                      >
                        Remover
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.mediaButton}
                      onClick={() =>
                        mediaInputRef.current && mediaInputRef.current.click()
                      }
                    >
                      Escolher arquivo
                    </Button>
                  )}
                </div>

                <div className={classes.variablesBox}>
                  <Typography variant="body2" className={classes.variableTitle}>
                    {"Vari\u00e1veis dispon\u00edveis"}
                  </Typography>
                  <div className={classes.variableChips}>
                    {quickAnswerVariables.map(item => (
                      <Chip
                        key={item.label}
                        label={item.label}
                        clickable
                        className={classes.variableChip}
                        onClick={() => {
                          const currentMessage = String(values.message || "");
                          setFieldValue("message", currentMessage + item.value);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? `${i18n.t("quickAnswersModal.buttons.okEdit")}`
                    : `${i18n.t("quickAnswersModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;
